import * as Yup from 'yup';
import { Icon } from '@iconify/react';
import { useFormik, FormikProvider } from 'formik';
import saveIcon from '@iconify/icons-eva/save-outline';
// material
import { Autocomplete, Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { editItem, createTokenClub } from '../../fetch';
import { groupsActions } from '../../store';
import { useTranslation } from '../../common/LocalizationProvider';
import CountrySelect from '../CountrySelect';
import { selectAerosArray } from '../../createSelectorUtils';

// ----------------------------------------------------------------------

const initialValues = {
  name: '',
  adresse: '',
  adresseSuite: '',
  ville: '',
  codePostal: '',
  phone: '',
  email: '',
  siteWeb: '',
  distanceLimit: 100,
  country: 'FR',
  aero: ''
};

// ----------------------------------------------------------------------

ClubForm.propTypes = {
  groupId: PropTypes.number,
  group: PropTypes.object
};

export default function ClubForm({ groupId, group }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const aeros = useSelector(selectAerosArray);

  const RegisterSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, t('yupValid_Short'))
      .max(50, t('yupValid_Long'))
      .required(t('yupValid_Required')),
    codePostal: Yup.number(t('yupValid_Invalid')),
    email: Yup.string().email(t('yupValid_Invalid')),
    distanceLimit: Yup.number(t('yupValid_Invalid')),
    country: Yup.string().required(t('yupValid_Required'))
  });

  const formik = useFormik({
    initialValues:
      group !== undefined
        ? {
            ...group,
            name: group.name === null ? '' : group.name,
            adresse: group.adresse === null ? '' : group.adresse,
            adresseSuite: group.adresseSuite === null ? '' : group.adresseSuite,
            ville: group.ville === null ? '' : group.ville,
            codePostal: group.codePostal === null ? '' : group.codePostal,
            phone: group.phone === null ? '' : group.phone,
            email: group.email === null ? '' : group.email,
            siteWeb: group.siteWeb === null ? '' : group.siteWeb,
            distanceLimit: group.distanceLimit === null ? '' : group.distanceLimit,
            country: group.country === null ? '' : group.country,
            aero: group.aero === null ? '' : group.aero
          }
        : initialValues,
    validationSchema: RegisterSchema,
    onSubmit: async (values) => {
      const response = await editItem('groups', groupId, values);
      if (response.ok) {
        enqueueSnackbar(
          group !== undefined ? t('addClub_Snackbar_Change') : t('addClub_Snackbar_Add'),
          {
            variant: 'success'
          }
        );

        const result = await response.json();
        dispatch(groupsActions.update([result]));

        if (group === undefined) {
          const responseToken = await createTokenClub(result.id);

          if (responseToken.ok) {
            enqueueSnackbar(t('CL_Snackbar_Token_Create'), { variant: 'success' });
            dispatch(groupsActions.updateToken([await responseToken.json()]));
          } else {
            const error = await responseToken.text();
            const message = error.slice(0, error.indexOf('-'));
            enqueueSnackbar(message, { variant: 'error' });
          }
        }
        navigate('/club', { replace: true });
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
        setSubmitting(false);
      }
    }
  });
  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setSubmitting,
    setFieldValue
  } = formik;

  return (
    <FormikProvider value={formik}>
      <Stack spacing={3}>
        <TextField
          fullWidth
          label={t('fields_Name')}
          {...getFieldProps('name')}
          error={Boolean(touched.name && errors.name)}
          helperText={touched.name && errors.name}
        />

        <TextField
          fullWidth
          autoComplete="address"
          label={t('fields_Address')}
          {...getFieldProps('adresse')}
          error={Boolean(touched.adresse && errors.adresse)}
          helperText={touched.adresse && errors.adresse}
        />
        <TextField
          fullWidth
          autoComplete="address"
          label={t('fields_ExtraAddress')}
          {...getFieldProps('adresseSuite')}
          error={Boolean(touched.adresseSuite && errors.adresseSuite)}
          helperText={touched.adresseSuite && errors.adresseSuite}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label={t('fields_City')}
            autoComplete="city"
            {...getFieldProps('ville')}
            error={Boolean(touched.ville && errors.ville)}
            helperText={touched.ville && errors.ville}
          />

          <TextField
            fullWidth
            label={t('fields_Zip')}
            autoComplete="zipCode"
            type="number"
            {...getFieldProps('codePostal')}
            error={Boolean(touched.codePostal && errors.codePostal)}
            helperText={touched.codePostal && errors.codePostal}
          />
        </Stack>

        <CountrySelect formik={formik} />

        <Autocomplete
          fullWidth
          {...getFieldProps('aero')}
          options={aeros}
          getOptionLabel={(option) =>
            option.oaci === undefined ? '' : `${option.oaci} - ${option.nom}`
          }
          value={aeros.find((aero) => aero.oaci === getFieldProps('aero').value) || null}
          renderInput={(params) => <TextField {...params} label={t('fields_Aerodrome')} />}
          onChange={(event, value) => setFieldValue('aero', value !== null ? value.oaci : 0)}
        />

        <TextField
          fullWidth
          autoComplete="telephone"
          type="number"
          label={t('fields_Phone')}
          {...getFieldProps('phone')}
          error={Boolean(touched.phone && errors.phone)}
          helperText={touched.phone && errors.phone}
        />

        <TextField
          fullWidth
          autoComplete="email"
          type="email"
          label={t('fields_Email')}
          {...getFieldProps('email')}
          error={Boolean(touched.email && errors.email)}
          helperText={touched.email && errors.email}
        />

        <TextField
          fullWidth
          autoComplete="website"
          type="website"
          label={t('fields_Website')}
          {...getFieldProps('siteWeb')}
        />

        <TextField
          fullWidth
          type="number"
          label={t('fields_Distance')}
          {...getFieldProps('distanceLimit')}
          error={Boolean(touched.distanceLimit && errors.distanceLimit)}
          helperText={touched.distanceLimit && errors.distanceLimit}
        />

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          startIcon={<Icon icon={saveIcon} />}
          onClick={handleSubmit}
        >
          {t('btn_Save')}
        </LoadingButton>
      </Stack>
    </FormikProvider>
  );
}
